import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShare,
    faCheck,
    faUsers,
    faMoneyBillWave,
    faCalendarAlt,
    faLock,
    faPlus,
    faFire,
    faTrash,
    faSpinner,
    faCrown,
} from '@fortawesome/free-solid-svg-icons';
import { EventWithId } from '../../hooks/useEvents';
import { useEventUsersCache } from '../../hooks/useEventUsersCache';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { resolveParticipantNames } from '../../utils/helpers';

interface EventCardProps {
    event: EventWithId;
    copiedEventId: string | null;
    onShareLink: (eventId: string) => void;
    onDeleteEvent?: (eventId: string) => Promise<void>;
    onRemoveHistory?: (eventId: string) => Promise<void>;
    currentUserId?: string;
    isClosed: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
                                                 event,
                                                 copiedEventId,
                                                 onShareLink,
                                                 onDeleteEvent,
                                                 onRemoveHistory,
                                                 currentUserId,
                                                 isClosed
                                             }) => {
    const isOwner = currentUserId ? event.meta.createdBy === currentUserId : false;
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { t, i18n } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    // Names come from eventUsers where present: that node is the only one a
    // participant who does not own the event can rename themselves in, and it is
    // live, so the card follows a rename without a reload.
    const { eventUsers, loading: namesLoading } = useEventUsersCache(event.id);

    const { participantIds, participantNames } = useMemo(() => {
        const names = resolveParticipantNames(event.meta.participants, eventUsers);
        return { participantIds: Object.keys(names), participantNames: names };
    }, [event.meta.participants, eventUsers]);

    const calculateEventTotal = (): number => {
        if (!event.expenses) return 0;
        return Object.values(event.expenses).reduce((sum, expense) => sum + expense.amount, 0);
    };

    const getStatusIcon = () => {
        if (isClosed) {
            return {
                status: t('myEvents.eventCard.status.closed'),
                icon: faLock,
                iconClass: 'text-gray-400 dark:text-gray-500',
            };
        }

        const expenseCount = event.expenses ? Object.keys(event.expenses).length : 0;
        if (expenseCount === 0) {
            return {
                status: t('myEvents.eventCard.status.new'),
                icon: faPlus,
                iconClass: 'text-blue-500 dark:text-blue-400',
            };
        }

        return {
            status: t('myEvents.eventCard.status.active'),
            icon: faFire,
            iconClass: 'text-orange-500 dark:text-orange-400',
        };
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!event.id) return;
        setIsDeleting(true);
        try {
            if (isOwner && onDeleteEvent) {
                await onDeleteEvent(event.id);
            } else if (!isOwner && onRemoveHistory) {
                await onRemoveHistory(event.id);
            }
        } catch (error) {
            console.error('Action failed:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleDeleteCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    const total = calculateEventTotal();
    const expenseCount = event.expenses ? Object.keys(event.expenses).length : 0;
    const { status, icon, iconClass } = getStatusIcon();

    // Sort participants: owner first
    const sortedParticipantIds = useMemo(() => {
        return [...participantIds].sort((a, b) => {
            if (a === event.meta.createdBy) return -1;
            if (b === event.meta.createdBy) return 1;
            return 0;
        });
    }, [participantIds, event.meta.createdBy]);

    const cardClass = `relative block cursor-pointer transition-all ${
        isOwner 
            ? 'glass-card border-2 !border-indigo-400 dark:!border-indigo-500 hover:ring-4 hover:ring-indigo-400/30' 
            : 'glass-card hover:ring-2 hover:ring-indigo-400/30'
    }`;

    return (
        <Link to={`/event/${event.id}`} className={cardClass}>
            {/* Delete Confirmation Modal — rendered via portal to body */}
            {showDeleteConfirm && createPortal(
                <div 
                    className="glass-modal-overlay"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteCancel(e);
                    }}
                >
                    <div 
                        className="glass-modal p-6 max-w-sm w-full mx-4"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                            {isOwner
                                ? t('myEvents.eventCard.deleteConfirm.title', { name: event.meta.name })
                                : t('myEvents.eventCard.removeConfirm.title', { name: event.meta.name })}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-5">
                            {isOwner
                                ? t('myEvents.eventCard.deleteConfirm.message')
                                : t('myEvents.eventCard.removeConfirm.message')}
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={handleDeleteCancel}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 text-sm font-medium"
                            >
                                {isOwner
                                    ? t('myEvents.eventCard.deleteConfirm.cancel')
                                    : t('myEvents.eventCard.removeConfirm.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center text-sm font-medium"
                            >
                                {isDeleting ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                ) : (
                                    isOwner
                                        ? t('myEvents.eventCard.deleteConfirm.confirm')
                                        : t('myEvents.eventCard.removeConfirm.confirm')
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <div className="p-4 flex flex-col h-full">
                {/* Event Header — Status icon inline before title */}
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-snug flex items-center gap-2 min-w-0">
                        <FontAwesomeIcon
                            icon={icon}
                            className={`${iconClass} text-sm flex-shrink-0`}
                            title={status}
                        />
                        <span className="truncate">{event.meta.name}</span>
                    </h3>

                    {/* Compact action buttons — icon only */}
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.preventDefault()}>
                        <button
                            onClick={(e) => { e.preventDefault(); onShareLink(event.id); }}
                            className={`w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all ${
                                copiedEventId === event.id
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400'
                            }`}
                            title={copiedEventId === event.id ? t('myEvents.eventCard.copied') : t('myEvents.eventCard.share')}
                        >
                            <FontAwesomeIcon
                                icon={copiedEventId === event.id ? faCheck : faShare}
                                className="text-sm"
                            />
                        </button>

                        {((isOwner && onDeleteEvent) || (!isOwner && onRemoveHistory)) && (
                            <button
                                onClick={handleDeleteClick}
                                disabled={isDeleting}
                                className="w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400 transition-all disabled:opacity-50"
                                title={isOwner ? t('myEvents.eventCard.deleteConfirm.confirm') : t('myEvents.eventCard.removeConfirm.confirm')}
                            >
                                <FontAwesomeIcon icon={isDeleting ? faSpinner : faTrash} className={`text-sm ${isDeleting ? 'animate-spin' : ''}`} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Event Description */}
                {event.meta.description && (
                    <p className="text-gray-500 dark:text-gray-400 text-xs mb-2.5 line-clamp-1 leading-relaxed">
                        {event.meta.description}
                    </p>
                )}

                {/* Compact Stats — single line with dot separators */}
                <div className="flex items-center flex-wrap gap-x-1 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span className="flex items-center whitespace-nowrap">
                        <FontAwesomeIcon icon={faUsers} className="mr-1 text-blue-500/70" />
                        <span className="font-medium text-gray-700 dark:text-gray-300">{participantIds.length}</span>
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="flex items-center whitespace-nowrap">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1 text-green-500/70" />
                        <span className="font-medium text-gray-700 dark:text-gray-300">{expenseCount}</span>
                        {total > 0 && (
                            <span className="ml-1 font-semibold text-green-600 dark:text-green-400">
                                ({formatCurrency(total)})
                            </span>
                        )}
                    </span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="flex items-center whitespace-nowrap">
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-purple-500/70" />
                        {new Date(event.meta.createdAt).toLocaleDateString(i18n.language || 'en', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        })}
                    </span>
                </div>

                {/* Participants Chips — no label, owner bold+accent */}
                <div className="flex-1">
                    {namesLoading && Array.isArray(event.meta.participants) ? (
                        <div className="text-xs text-gray-400 dark:text-gray-500">{t('myEvents.eventCard.loadingNames')}</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {sortedParticipantIds.slice(0, 5).map((participantId) => {
                                const isParticipantOwner = participantId === event.meta.createdBy;
                                return (
                                    <span
                                        key={participantId}
                                        className={`px-2 py-0.5 text-xs rounded-md inline-flex items-center gap-1 ${
                                            isParticipantOwner
                                                ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-200 dark:border-indigo-700/50'
                                                : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-400 border border-gray-200/60 dark:border-gray-600/40'
                                        }`}
                                    >
                                        {isParticipantOwner && (
                                            <FontAwesomeIcon icon={faCrown} className="text-[9px] text-indigo-500 dark:text-indigo-400" />
                                        )}
                                        {participantNames[participantId] || participantId}
                                    </span>
                                );
                            })}
                            {participantIds.length > 5 && (
                                <span className="px-2 py-0.5 text-xs rounded-md bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 border border-gray-200/60 dark:border-gray-600/40">
                                    {t('myEvents.eventCard.more', { count: participantIds.length - 5 })}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

export default EventCard;